import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, Check, Send } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";

interface ExternalNotesCardProps {
  booking: any;
  onUpdate?: () => void;
  canEdit?: boolean;
}

export function ExternalNotesCard({
  booking,
  onUpdate,
  canEdit = true
}: ExternalNotesCardProps) {
  const [notes, setNotes] = useState(booking?.special_request || "");
  const [isSendingSave, setIsSendingSave] = useState(false); // State for button loader
  const [showSaved, setShowSaved] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const lastSavedValue = useRef(booking?.special_request || "");

  // Update notes when booking changes
  useEffect(() => {
    setNotes(booking?.special_request || "");
    lastSavedValue.current = booking?.special_request || "";
  }, [booking?.special_request, booking?.id]);

  const handleSave = async (notesToSave: string, sendNotification: boolean = false) => {
    // Don't save if the value hasn't changed (unless we're sending notification)
    if (notesToSave === lastSavedValue.current && !sendNotification) {
      return;
    }

    // Only show loader in button when sending notification
    if (sendNotification) {
      setIsSendingSave(true);
    }
    try {
      // Get venue ID from the booking or URL
      let venueId = booking?.studio_show_host_bookings?.[0]?.studios?.venue_id ||
                   booking?.studio_show_host_bookings?.[0]?.studios?.venues?.id;

      if (!venueId || venueId === 0) {
        const pathMatch = window.location.pathname.match(/\/venue\/(\d+)\//);
        venueId = pathMatch ? pathMatch[1] : "0";
      }

      const response = await fetch(
        `/api/staff/venues/${venueId}/bookings/${booking.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            notes: notesToSave.trim() || null,  // API expects 'notes', not 'special_request'
            sendNotesNotification: sendNotification, // Control when to send email
            changedFields: {
              isNotesChange: true,  // Indicate that notes have been changed
            }
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("ExternalNotesCard - Save failed:", errorText);
        throw new Error("Failed to update external notes");
      }

      // Update the last saved value
      lastSavedValue.current = notesToSave;

      // Show success toast when notification is sent
      if (sendNotification) {
        toast.success("Notes saved and customer notified");
      } else {
        // For autosave, show saved indicator
        setShowSaved(true);
        setTimeout(() => setShowSaved(false), 2000);
      }

      // Call onUpdate if provided
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Error updating external notes:", error);
      toast.error("Failed to save external notes");
    } finally {
      setIsSendingSave(false);
    }
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newNotes = e.target.value;
    setNotes(newNotes);

    // Only set up auto-save if user can edit
    if (!canEdit) {
      return;
    }

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for auto-save (1.5 seconds after user stops typing)
    // Autosave WITHOUT sending notification
    debounceTimer.current = setTimeout(() => {
      handleSave(newNotes, false); // false = don't send notification
    }, 1500);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">External Notes</CardTitle>
          {canEdit && showSaved && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Check className="h-4 w-4 text-green-500" />
              <span className="text-green-500">Saved</span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Textarea
          id="external-notes"
          value={notes}
          onChange={handleNotesChange}
          placeholder="Enter any notes or special requests that should be visible to the customer..."
          className="min-h-[100px]"
          disabled={!canEdit}
        />
        {canEdit && (
          <div className="mt-4 flex justify-end">
            <Button
              onClick={() => handleSave(notes, true)}
              disabled={isSendingSave}
              size="sm"
              className="gap-2"
            >
              {isSendingSave ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Save & Notify Customer
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}